import axios from "axios";
import jsPDF from "jspdf";
import toast from "react-hot-toast";

const IOT_BASE = "https://api.livestockify.com/api/iot";
const COLS_PER_TABLE = 10;

const COLORS = {
  green: [52, 199, 89],
  greenDark: [10, 92, 51],
  slate: [30, 41, 59],
  muted: [100, 116, 139],
  line: [226, 232, 240],
  rowAlt: [248, 250, 252],
  white: [255, 255, 255],
};

const mapRangeToPeriod = (rangeType) => {
  if (rangeType === "Today" || rangeType === "Daily") return "today";
  if (rangeType === "Weekly") return "week";
  if (rangeType === "Monthly") return "month";
  if (rangeType === "Custom") return "custom";
  return "today";
};

const formatValue = (val) => {
  if (val === null || val === undefined || val === "-") return "-";
  const num = Number(val);
  if (!Number.isFinite(num)) return String(val);
  return num.toFixed(3);
};

const formatTimestamp = (ts) => {
  if (!ts) return "-";
  return new Date(ts).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Short sensor labels for PDF table headers
 *  Power Phase 1 → PP1, Current Phase1 → CP1, Frequency1 → Freq1
 */
const SENSOR_SHORT_LABELS = {
  powera: "PPA",
  powerb: "PPB",
  powerc: "PPC",
  currenta: "CPA",
  currentb: "CPB",
  currentc: "CPC",
  voltagea: "VPA",
  voltageb: "VPB",
  voltagec: "VPC",
  powerphasea: "PPA",
  powerphaseb: "PPB",
  powerphasec: "PPC",
  powerphase1: "PP1",
  powerphase2: "PP2",
  powerphase3: "PP3",
  currentphasea: "CPA",
  currentphaseb: "CPB",
  currentphasec: "CPC",
  currentphase1: "CP1",
  currentphase2: "CP2",
  currentphase3: "CP3",
  voltagephasea: "VPA",
  voltagephaseb: "VPB",
  voltagephasec: "VPC",
  voltagephase1: "VP1",
  voltagephase2: "VP2",
  voltagephase3: "VP3",
  frequency: "Freq",
  frequency1: "Freq1",
  frequency2: "Freq2",
  frequency3: "Freq3",
  totalpower: "TotP",
  totalenergy: "TotE",
  totalenergykwh: "TotkWh",
  powerfactor: "PF",
  pf: "PF",
  running: "Run",
  sessioncount: "Sess#",
  minuteenergywh: "MinWh",
  sessionenergywh: "SessWh",
  totalactivepowerreg: "ActP",
};

const shortSensorName = (key) => {
  if (!key) return "-";
  const normalized = String(key).toLowerCase().replace(/[\s_\-]/g, "");
  if (SENSOR_SHORT_LABELS[normalized]) return SENSOR_SHORT_LABELS[normalized];

  // Frequency1 / Frequency_2 → Freq1 / Freq2
  const freqMatch = normalized.match(/^frequency(\d+)$/);
  if (freqMatch) return `Freq${freqMatch[1]}`;

  // Power Phase 1 / CurrentPhase1 / Voltage_Phase_A → PP1 / CP1 / VPA
  const phaseMatch = normalized.match(
    /^(power|current|voltage|pwr|amp|volt)(phase)?([abc123])$/,
  );
  if (phaseMatch) {
    const typeMap = {
      power: "PP",
      pwr: "PP",
      current: "CP",
      amp: "CP",
      voltage: "VP",
      volt: "VP",
    };
    return `${typeMap[phaseMatch[1]]}${String(phaseMatch[3]).toUpperCase()}`;
  }

  const parts = String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(/[\s_\-]+/)
    .filter(Boolean);
  if (parts.length > 1) {
    return parts
      .map((p) => p.slice(0, Math.max(1, Math.floor(8 / parts.length))))
      .join("")
      .slice(0, 8);
  }
  return String(key).slice(0, 8);
};

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks.length ? chunks : [[]];
};

export async function downloadIotDeviceReportPdf({
  token,
  device,
  rangeType,
  startDate,
  endDate,
}) {
  if (!device) {
    toast.error("Please select a device before downloading the report.");
    return;
  }
  if (!token) {
    toast.error("Session expired. Please login again.");
    return;
  }

  try {
    const period = mapRangeToPeriod(rangeType);
    const params = { period };

    if (period === "custom") {
      if (!startDate || !endDate) {
        toast.error("Please select a valid custom date range.");
        return;
      }
      params.st = startDate;
      params.et = endDate;
    }

    const res = await axios.get(`${IOT_BASE}/data/period/${device.deviceId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });

    const deviceRecords = Array.isArray(res.data)
      ? res.data
      : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

    if (!deviceRecords.length) {
      toast.error("No data found for this device in the selected range.");
      return;
    }

    const sortedRecords = [...deviceRecords].sort((a, b) => {
      const ta = new Date(a.timestamp || 0).getTime();
      const tb = new Date(b.timestamp || 0).getTime();
      return ta - tb;
    });

    const sensorKeys = [];
    const keySet = new Set();
    sortedRecords.forEach((record) => {
      Object.keys(record.sensors || {}).forEach((key) => {
        if (key.toLowerCase() === "timestamp") return;
        if (!keySet.has(key)) {
          keySet.add(key);
          sensorKeys.push(key);
        }
      });
    });

    const pdf = new jsPDF("l", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginX = 14;
    const marginTop = 14;
    const contentWidth = pageWidth - marginX * 2;
    const gap = 4;
    let y = 0;
    let pageNum = 1;

    const drawTopBar = () => {
      pdf.setFillColor(...COLORS.greenDark);
      pdf.rect(0, 0, pageWidth, 7, "F");
      pdf.setFillColor(...COLORS.green);
      pdf.rect(0, 7, pageWidth, 1.8, "F");
    };

    const drawFooter = () => {
      pdf.setDrawColor(...COLORS.line);
      pdf.setLineWidth(0.4);
      pdf.line(marginX, pageHeight - 11, pageWidth - marginX, pageHeight - 11);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(...COLORS.muted);
      pdf.text("Urjabio Energy Monitoring", marginX, pageHeight - 5.5);
      pdf.text(
        `Generated ${new Date().toLocaleString("en-GB")}`,
        pageWidth / 2,
        pageHeight - 5.5,
        { align: "center" },
      );
      pdf.text(`Page ${pageNum}`, pageWidth - marginX, pageHeight - 5.5, {
        align: "right",
      });
    };

    const ensureSpace = (needed, onNewPage) => {
      if (y + needed > pageHeight - 16) {
        drawFooter();
        pdf.addPage();
        pageNum += 1;
        drawTopBar();
        y = marginTop + 6;
        if (onNewPage) onNewPage();
      }
    };

    const writeTitleBlock = () => {
      drawTopBar();
      y = marginTop + 8;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.setTextColor(...COLORS.greenDark);
      pdf.text("Energy Meter Report", marginX, y);
      y += 7;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...COLORS.muted);
      pdf.text("Urjabio Live Device Analytics", marginX, y);
      y += 10;

      // Equal-width info cards with consistent gap
      const cardCount = 3;
      const cardW = (contentWidth - gap * (cardCount - 1)) / cardCount;
      const cardH = 16;
      const cards = [
        { label: "DEVICE", value: device.name || "-" },
        { label: "DEVICE ID", value: String(device.deviceId || "-") },
        {
          label: "RANGE",
          value:
            rangeType === "Custom"
              ? `${startDate} → ${endDate}`
              : String(rangeType),
        },
      ];

      cards.forEach((card, i) => {
        const x = marginX + i * (cardW + gap);
        pdf.setFillColor(...COLORS.rowAlt);
        pdf.setDrawColor(...COLORS.line);
        pdf.setLineWidth(0.4);
        pdf.roundedRect(x, y, cardW, cardH, 2.5, 2.5, "FD");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7);
        pdf.setTextColor(...COLORS.green);
        pdf.text(card.label, x + 4, y + 5.5);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(...COLORS.slate);
        pdf.text(String(card.value).slice(0, 30), x + 4, y + 12);
      });
      y += cardH + 6;

      // Stats bar aligned to same width as cards
      pdf.setFillColor(...COLORS.green);
      pdf.roundedRect(marginX, y, contentWidth, 11, 2.5, 2.5, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...COLORS.white);
      pdf.text(
        `${sortedRecords.length} records   •   ${sensorKeys.length} sensors   •   Energy meter monitoring`,
        marginX + 5,
        y + 7.2,
      );
      y += 16;
    };

    const ROW_H = 9.5;
    const HEADER_H = 11;

    const writeTableHeader = (keys, colWidth, timestampW) => {
      pdf.setFillColor(...COLORS.greenDark);
      pdf.roundedRect(marginX, y, contentWidth, HEADER_H, 2, 2, "F");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7);
      pdf.setTextColor(...COLORS.white);
      pdf.text("TIME", marginX + 4, y + HEADER_H / 2 + 2.2);
      keys.forEach((key, i) => {
        const x = marginX + timestampW + i * colWidth + 2;
        pdf.text(shortSensorName(key), x, y + HEADER_H / 2 + 2.2);
      });
      y += HEADER_H + 3;
    };

    // Data tables only
    writeTitleBlock();

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(...COLORS.greenDark);
    pdf.text("Sensor Data Table", marginX, y);
    y += 8;

    const sensorChunks = chunkArray(sensorKeys, COLS_PER_TABLE);
    const timestampW = 46;
    const tableRecords = [...sortedRecords].reverse();

    sensorChunks.forEach((keys, chunkIndex) => {
      if (chunkIndex > 0) {
        drawFooter();
        pdf.addPage();
        pageNum += 1;
        writeTitleBlock();
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(...COLORS.greenDark);
        pdf.text("Sensor Data Table", marginX, y);
        y += 8;
      }

      const colWidth =
        (contentWidth - timestampW) / Math.max(keys.length, 1);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(...COLORS.slate);
      pdf.text(
        `Sensors ${chunkIndex * COLS_PER_TABLE + 1}–${
          chunkIndex * COLS_PER_TABLE + keys.length
        }`,
        marginX,
        y,
      );
      y += 6;

      writeTableHeader(keys, colWidth, timestampW);

      tableRecords.forEach((record, rowIndex) => {
        ensureSpace(ROW_H + 2, () =>
          writeTableHeader(keys, colWidth, timestampW),
        );

        const rowTop = y - 2.5;
        if (rowIndex % 2 === 0) {
          pdf.setFillColor(...COLORS.rowAlt);
          pdf.rect(marginX, rowTop, contentWidth, ROW_H, "F");
        }

        const textY = rowTop + ROW_H / 2 + 2.2;
        const sensors = record.sensors || {};
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(...COLORS.slate);
        pdf.text(formatTimestamp(record.timestamp), marginX + 4, textY);

        keys.forEach((key, i) => {
          pdf.setTextColor(...COLORS.greenDark);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(7.5);
          pdf.text(
            formatValue(sensors[key]),
            marginX + timestampW + i * colWidth + 2,
            textY,
          );
        });
        y += ROW_H;
      });

      y += 8;
    });

    drawFooter();
    pdf.save(`${device.name || "device"}_Urjabio_Report.pdf`);
    toast.success("Report downloaded successfully!");
  } catch (error) {
    console.error("Error downloading Urjabio report:", error);
    toast.error("Failed to generate PDF report. Please try again.");
  }
}

export async function downloadCrusherReportPdf() {
  toast.error("Crusher reports are not available for Urjabio.");
}
